import { CoherencePanel, CoherencePanelSize } from '@coherence-design-system/controls';
import {
  CheckboxVisibility,
  DatePicker,
  DetailsListLayoutMode,
  DetailsRow,
  IColumn,
  IDetailsRowProps,
  PivotItem,
  SelectionMode,
  ShimmeredDetailsList,
  Text,
} from '@fluentui/react';
import { DefaultButton } from '@micro-frontend-react/employee-experience/lib/Buttons/DefaultButton';
import { PrimaryButton } from '@micro-frontend-react/employee-experience/lib/Buttons/PrimaryButton';
import { Context } from '@micro-frontend-react/employee-experience/lib/Context';
import { IEmployeeExperienceContext } from '@micro-frontend-react/employee-experience/lib/IEmployeeExperienceContext';
import * as React from 'react';
import { useParams } from 'react-router-dom';

import { IMachineLearningExperimentRunResult } from '../../../Common/MachineLearningModels/MachineLearningExperimentRunResult';
import { MachineLearningLabelOption } from '../../../Common/MachineLearningModels/MachineLearningLabelOption';
import * as StyledMGMT from '../../../Common/Styles/portalManagement.styled';
import * as Styled from '../../../Common/Styles/portalPage.styled';
import { useMachineLearningExperiment } from '../../../Hooks/MachineLearning/useMachineLearningExperiment';
import { useMachineLearningExperimentResults } from '../../../Hooks/MachineLearning/useMachineLearningExperimentResults';

export function MachineLearningExperimentResults(): React.ReactElement {
  const [selectedDate, setDate] = React.useState<Date>(new Date());
  const [selectedLabel, selectLabel] = React.useState<string>('Unlabeled');
  const [selectedRecord, setSelectedRecord] = React.useState<IMachineLearningExperimentRunResult | null>(null);
  const params = useParams<{ experimentId: string }>();
  const experiment = useMachineLearningExperiment(params.experimentId);
  const [experiments, reload] = useMachineLearningExperimentResults(params.experimentId, selectedDate);
  const { httpClient } = React.useContext(Context as React.Context<IEmployeeExperienceContext>);

  const categorizedResults = React.useMemo(() => {
    const result: { [key: string]: IMachineLearningExperimentRunResult[] } = {
      Unlabeled: [],
    };

    for (const experiment of experiments || []) {
      if (experiment.labels.length == 0) {
        result.Unlabeled.push(experiment);
      } else {
        result[experiment.labels[0]] = result[experiment.labels[0]] || [];
        result[experiment.labels[0]].push(experiment);
      }
    }

    return result;
  }, [experiments]);

  const onRenderItemColumn = React.useCallback(
    (item: IMachineLearningExperimentRunResult, index: number, column: IColumn): React.ReactNode => {
      if (column.key == 'id') {
        return (
          <div aria-label={`Column header: ${column.key}. Cell content: ${item.id}`}>
            <ButtonLink onClick={() => setSelectedRecord(item)}>{item.id}</ButtonLink>
          </div>
        );
      }
      if (column.key === 'labels') {
        return <Text>{(item.labels || []).join(', ')}</Text>;
      }
      return <Text>{item[column.key as keyof Omit<IMachineLearningExperimentRunResult, 'content'>]}</Text>;
    },
    []
  );

  async function handleLabelSelected(selectedLabel: string) {
    setSelectedRecord({ ...selectedRecord, labels: [selectedLabel] });

    await httpClient.request({
      url: `${__API_BASE_URL__}/machine-learning/experiments/${params.experimentId}/results/${selectedRecord.id}`,
      resource: __CLIENT_ID__,
      method: 'PUT',
      data: {
        labels: [selectedLabel],
      },
    });

    await reload();
  }

  return (
    <div>
      <title>Machine Learning</title>
      <Styled.PortalPageHeadingSection>
        <Styled.PortalPageTitleText>Results for {experiment?.type}</Styled.PortalPageTitleText>
      </Styled.PortalPageHeadingSection>
      <div
        style={{
          background: '#f2f9ff',
          paddingLeft: 5,
        }}
      >
        <DatePicker
          label="Run date"
          ariaLabel="Start date"
          placeholder="Select a date..."
          value={selectedDate}
          styles={{
            root: {
              paddingBottom: 20,
              paddingLeft: 100,
              marginLeft: -8,
              width: 300,
            },
          }}
          onSelectDate={(date) => {
              setDate(date)
          }}
        />
      </div>
      <StyledMGMT.PortalManagementPivot
        aria-label="Data Owner Request Tables Pivot"
        selectedKey={selectedLabel}
        onLinkClick={(item) => selectLabel(item.props.itemKey)}
        defaultSelectedKey="Requests"
      >
        <PivotItem
          headerText="Unlabeled"
          itemKey="Unlabeled"
          headerButtonProps={{
            'data-title': 'Unlabeled',
          }}
        />
        {(MachineLearningLabelOption[experiment?.type] ?? []).map((label) => (
          <PivotItem
            headerText={label}
            itemKey={label}
            headerButtonProps={{
              'data-title': label,
            }}
          />
        ))}
      </StyledMGMT.PortalManagementPivot>
      <Styled.ButtonsTableSection>
        <ShimmeredDetailsList
          items={categorizedResults[selectedLabel] || []}
          enableShimmer={false}
          columns={experimentTableColumns}
          onRenderItemColumn={onRenderItemColumn}
          onRenderRow={onRenderRow}
          selectionMode={SelectionMode.none}
          layoutMode={DetailsListLayoutMode.justified}
          checkboxVisibility={CheckboxVisibility.hidden}
          ariaLabelForShimmer="Data Owners Table is being fetched"
          ariaLabelForGrid="Data Owners Table"
        />
      </Styled.ButtonsTableSection>

      <CoherencePanel
        isOpen={selectedRecord != null}
        onDismiss={() => setSelectedRecord(null)}
        panelSize={CoherencePanelSize.medium}
        closeButtonAriaLabel="Close"
        data-testid="add-data-owner-panel"
      >
        {selectedRecord != null && (
          <div>
            <PanelHeading>Data Label</PanelHeading>

            <LabelOptions>
              {(MachineLearningLabelOption[experiment?.type] ?? []).map((label) => {
                if (selectedRecord.labels.includes(label)) {
                  return (
                    <PrimaryButton
                      title={`Label data as ${label}`}
                      onClick={() => {
                        // intentionally blank
                      }}
                      usageEvent={{
                        feature: 'HRDP',
                        subFeature: 'HRDP.MachineLearningExperiments.Label',
                      }}
                    >
                      {label}
                    </PrimaryButton>
                  );
                } else {
                  return (
                    <DefaultButton
                      title={`Label data as ${label}`}
                      onClick={() => handleLabelSelected(label)}
                      usageEvent={{
                        feature: 'HRDP',
                        subFeature: 'HRDP.MachineLearningExperiments.Label',
                      }}
                    >
                      {label}
                    </DefaultButton>
                  );
                }
              })}
            </LabelOptions>

            <Data>
              <b>Record</b>: {selectedRecord.id}
            </Data>

            {Object.keys(selectedRecord.content).map((key) => {
              return (
                <Data key={key}>
                  <b>{key}</b>: {selectedRecord.content[key] || 'null'}
                </Data>
              );
            })}
          </div>
        )}
      </CoherencePanel>
    </div>
  );
}

const onRenderRow = (props: IDetailsRowProps) => {
  return <DetailsRow aria-busy={true} {...props} />;
};

const experimentTableColumns: IColumn[] = [
  {
    key: 'id',
    name: 'Result Id',
    minWidth: 300,
    maxWidth: 300,
  },
  {
    key: 'confidenceScore',
    name: 'Confidence Score',
    minWidth: 250,
    maxWidth: 250,
  },
];

const ButtonLink = styled.button`
  color: rgb(0, 120, 212);
  background: none;
  border: none;
  font-size: 14px;
  line-height: 20px;
`;

const LabelOptions = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  flex-wrap: wrap;
  margin-bottom: 20px;
`;

const Data = styled.div`
  margin-bottom: 10px;
`;

const PanelHeading = styled.h2`
  margin-bottom: 30px;
`;
